/**
 * ATELIER LUXURY SHOPIFY THEME - CART DRAWER ENGINE
 */

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.drawer = this;
    this.closeBtn = this.querySelector('[data-cart-close]');
    this.backdrop = this.querySelector('.cart-drawer__backdrop');
    this.initEvents();
  }

  initEvents() {
    this.closeBtn?.addEventListener('click', () => this.close());
    this.backdrop?.addEventListener('click', () => this.close());

    // Intercept clicks inside drawer for quantity +/- and remove
    this.addEventListener('click', (e) => {
      const minusBtn = e.target.closest('.quantity-btn--minus');
      const plusBtn = e.target.closest('.quantity-btn--plus');
      const removeBtn = e.target.closest('[data-cart-remove]');

      if (minusBtn) {
        e.preventDefault();
        const input = minusBtn.parentElement.querySelector('.quantity-field');
        const key = input.dataset.lineKey;
        const currentVal = parseInt(input.value, 10);
        if (currentVal > 1) {
          this.updateQuantity(key, currentVal - 1);
        } else {
          this.updateQuantity(key, 0);
        }
      }

      if (plusBtn) {
        e.preventDefault();
        const input = plusBtn.parentElement.querySelector('.quantity-field');
        const key = input.dataset.lineKey;
        const currentVal = parseInt(input.value, 10);
        this.updateQuantity(key, currentVal + 1);
      }

      if (removeBtn) {
        e.preventDefault();
        const key = removeBtn.dataset.lineKey;
        this.updateQuantity(key, 0);
      }
    });

    // Cart Note with debounce
    const noteField = this.querySelector('[data-cart-note]');
    if (noteField) {
      noteField.addEventListener('input', debounce((e) => {
        this.updateNote(e.target.value);
      }, 600));
    }
  }

  open() {
    this.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  async updateQuantity(lineKey, quantity) {
    try {
      this.classList.add('is-loading');
      const response = await fetch(window.routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: quantity,
          sections: 'cart-drawer'
        })
      });

      const data = await response.json();
      if (response.ok) {
        this.renderDrawer(data.sections['cart-drawer']);
        this.updateCartCount(data.item_count);
      } else {
        window.showToast(data.message || window.cartStrings.error);
      }
    } catch (err) {
      console.error('Cart update error:', err);
      window.showToast(window.cartStrings.error);
    } finally {
      this.classList.remove('is-loading');
    }
  }

  async updateNote(note) {
    try {
      await fetch(window.routes.cart_update_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ note: note })
      });
    } catch (err) {
      console.error('Error saving cart note:', err);
    }
  }

  async refresh() {
    try {
      const response = await fetch(`${window.routes.cart_url}?sections=cart-drawer`);
      const data = await response.json();
      if (data['cart-drawer']) {
        this.renderDrawer(data['cart-drawer']);
      }
    } catch (err) {
      console.error('Failed to refresh cart drawer:', err);
    }
  }

  renderDrawer(sectionHtml) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(sectionHtml, 'text/html');
    const newContent = doc.querySelector('.cart-drawer__wrapper');
    const currentWrapper = this.querySelector('.cart-drawer__wrapper');

    if (newContent && currentWrapper) {
      currentWrapper.innerHTML = newContent.innerHTML;
      this.initEvents();
    }
  }

  updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-hidden', count === 0);
    });
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

// Global hook for all Add-to-Cart forms
document.addEventListener('submit', async (e) => {
  const form = e.target.closest('[data-type="ajax-cart-form"]');
  if (!form) return;

  e.preventDefault();
  const submitBtn = form.querySelector('[type="submit"]');
  if (submitBtn) submitBtn.classList.add('is-loading');

  const formData = new FormData(form);
  formData.append('sections', 'cart-drawer');

  try {
    const res = await fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: formData
    });

    const data = await res.json();

    if (res.ok) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && window.themeSettings.cartType === 'drawer') {
        if (data.sections && data.sections['cart-drawer']) {
          drawer.renderDrawer(data.sections['cart-drawer']);
        } else {
          await drawer.refresh();
        }
        // Update count
        const cartRes = await fetch(`${window.routes.cart_url}.js`);
        const cart = await cartRes.json();
        drawer.updateCartCount(cart.item_count);
        drawer.open();
      } else {
        window.location.href = window.routes.cart_url;
      }
    } else {
      window.showToast(data.description || data.message || window.cartStrings.error);
    }
  } catch (err) {
    console.error('Error adding to cart:', err);
    window.showToast(window.cartStrings.error);
  } finally {
    if (submitBtn) submitBtn.classList.remove('is-loading');
  }
});

// Cart Drawer trigger in Header
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-cart-trigger]').forEach((trigger) => {
    trigger.addEventListener('click', (e) => {
      if (window.themeSettings.cartType === 'drawer') {
        e.preventDefault();
        const drawer = document.querySelector('cart-drawer');
        if (drawer) drawer.open();
      }
    });
  });
});
